import { VideoPlayer, type VideoPlayerHandle } from "@/components/shared/VideoPlayer";
import { GradingTestModal } from "@/components/slides/editor/canvas/GradingTestModal";
import { getSubtitleOverlayStyle } from "@/components/slides/list/subtitleOverlayStyle";
import { useAppSelector } from "@/store/hooks";
import {
  CAPTION_BUBBLE_PADDING_PERCENT,
  CAPTION_EDGE_INSET_PERCENT,
  CAPTION_HORIZONTAL_INSET_PERCENT,
  CAPTION_LINE_HEIGHT_RATIO,
  DEFAULT_RESPONSE_SUBTITLE_FONT_SCALE,
  DEFAULT_RESPONSE_SUBTITLE_PRESET,
  DEFAULT_SUBTITLE_POSITION,
  type GrammarResponseSlideData,
  SUBTITLE_FONT_SIZE_REFERENCE_WIDTH,
  SUBTITLE_PRESETS,
  type SubtitlePosition,
  type SubtitlePreset,
  clampSubtitleFontScale,
  clampSubtitleOffset,
  getSubtitleFontSize,
} from "@ai-teach/types";
import { CHECKPOINT_TRIGGER_TOLERANCE } from "@ai-teach/utils";
import { useEffect, useRef, useState } from "react";
import { IoMic } from "react-icons/io5";
import { MdVideoLibrary } from "react-icons/md";
import { LiveMobileViewContainer } from "../../../LiveMobileViewContainer";

// ── Main Component ────────────────────────────────────────────────────────────

/** `isStaticPreview`: this render will never play media (template picker thumbnail), so it opens
 *  on the prompt state instead of an empty video frame. */
export function GrammarResponseLiveMobileView({ isStaticPreview = false } = {}) {
  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const responseSlideData = slide?.slideData as GrammarResponseSlideData;

  // VideoPlayer (an HTML <video>) plays audio sources too, so checkpoint/subtitle
  // timeline logic is reused regardless of media type.
  const mediaSrc =
    responseSlideData.mediaType === "audio"
      ? responseSlideData.audio?.src
      : responseSlideData.video.src;

  const sortedCheckpoints = [...responseSlideData.checkpoints].sort(
    (a, b) => a.timestamp - b.timestamp
  );

  const [currentCheckpointIndex, setCurrentCheckpointIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(isStaticPreview);
  const [isAnswerModalOpen, setIsAnswerModalOpen] = useState(false);
  const [activeSubtitle, setActiveSubtitle] = useState<
    GrammarResponseSlideData["subtitles"][number] | null
  >(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const firstCheckpointImageSetRef = useRef(false);

  const currentCheckpoint = sortedCheckpoints[currentCheckpointIndex] ?? null;

  const videoRef = useRef<VideoPlayerHandle>(null);
  const lastPausedAt = useRef<number | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const [videoRenderedWidth, setVideoRenderedWidth] = useState<number | null>(null);

  useEffect(() => {
    videoRef.current?.play();
  }, []);

  useEffect(() => {
    const el = videoContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setVideoRenderedWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleTimeUpdate = (currentTime: number) => {
    const currentSubtitles = (slide?.slideData as GrammarResponseSlideData).subtitles.filter(
      (sub) => sub.startTime <= currentTime && sub.endTime >= currentTime
    );

    setActiveSubtitle(currentSubtitles[0] ?? null);

    // Show first checkpoint image after firstQuestionTime passes
    if (currentTime >= slide?.slideData.firstQuestionTime && !firstCheckpointImageSetRef.current) {
      firstCheckpointImageSetRef.current = true;
      setImageUrl(sortedCheckpoints[0]?.image?.src ?? null);
    }

    if (isPaused) return;
    if (!currentCheckpoint) return;
    if (currentTime < currentCheckpoint.timestamp) return;
    if (currentTime - currentCheckpoint.timestamp >= CHECKPOINT_TRIGGER_TOLERANCE) return;
    if (lastPausedAt.current === currentCheckpoint.timestamp) return;
    lastPausedAt.current = currentCheckpoint.timestamp;

    videoRef.current?.pause();
    videoRef.current?.seek(currentCheckpoint.timestamp);
    setIsPaused(true);
  };

  const handleContinue = () => {
    setIsAnswerModalOpen(false);
    lastPausedAt.current = null;
    if (currentCheckpoint?.skipTo != null) {
      videoRef.current?.seek(currentCheckpoint.skipTo);
    }
    videoRef.current?.play();
    setIsPaused(false);
    setCurrentCheckpointIndex((prev) => prev + 1);
    setImageUrl(null);
    setTimeout(() => {
      setImageUrl(responseSlideData.checkpoints[currentCheckpointIndex + 1]?.image.src ?? null);
    }, 1000);
  };

  const subtitlePosition =
    (activeSubtitle?.position as SubtitlePosition) ?? DEFAULT_SUBTITLE_POSITION;
  const subtitlePreset =
    (activeSubtitle?.preset as SubtitlePreset) ?? DEFAULT_RESPONSE_SUBTITLE_PRESET;
  const subtitleFontScale = clampSubtitleFontScale(
    activeSubtitle?.fontScale,
    DEFAULT_RESPONSE_SUBTITLE_FONT_SCALE
  );
  const subtitleOffset = clampSubtitleOffset(activeSubtitle?.offset);
  const captionStyle =
    SUBTITLE_PRESETS[subtitlePreset] ?? SUBTITLE_PRESETS[DEFAULT_RESPONSE_SUBTITLE_PRESET];
  const referenceWidth = videoRenderedWidth ?? SUBTITLE_FONT_SIZE_REFERENCE_WIDTH;
  const captionFontSize = getSubtitleFontSize(
    captionStyle.fontSize,
    subtitleFontScale,
    referenceWidth
  );
  const overlayPositionStyle = getSubtitleOverlayStyle(
    subtitlePosition,
    subtitleOffset,
    subtitlePosition === "top" ? CAPTION_EDGE_INSET_PERCENT.top : CAPTION_EDGE_INSET_PERCENT.bottom
  );
  const captionHorizontalInset = referenceWidth * (CAPTION_HORIZONTAL_INSET_PERCENT / 100);
  const captionBubblePadding = referenceWidth * (CAPTION_BUBBLE_PADDING_PERCENT / 100);

  return (
    <LiveMobileViewContainer>
      {/* Video */}
      <div
        ref={videoContainerRef}
        className="flex-1 aspect-[9/16] rounded-xl overflow-hidden relative"
      >
        {mediaSrc && (
          <VideoPlayer
            src={mediaSrc}
            ref={videoRef}
            onTimeUpdate={handleTimeUpdate}
            isPreview={true}
          />
        )}
        {!mediaSrc && !isStaticPreview && (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1">
            <MdVideoLibrary className="text-white/20 text-2xl" />
            <span className="text-[9px] font-medium text-white/40">
              No {responseSlideData.mediaType === "audio" ? "audio" : "video"}
            </span>
          </div>
        )}
        {activeSubtitle && (
          <div
            className="absolute left-0 right-0 flex justify-center pointer-events-none"
            style={{
              ...overlayPositionStyle,
              paddingLeft: captionHorizontalInset,
              paddingRight: captionHorizontalInset,
            }}
          >
            <div
              style={{
                backgroundColor: captionStyle.backgroundColor,
                borderRadius: 6,
                padding: `5px ${captionBubblePadding}px`,
              }}
              className="text-center"
            >
              <span
                style={{
                  color: captionStyle.textColor,
                  fontSize: captionFontSize,
                  lineHeight: `${captionFontSize * CAPTION_LINE_HEIGHT_RATIO}px`,
                  fontFamily: captionStyle.fontFamily,
                  fontWeight: captionStyle.fontWeight,
                  textShadow: captionStyle.textShadow
                    ? "0 1px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7)"
                    : undefined,
                  WebkitTextStroke: captionStyle.textOutline
                    ? `1.5px ${captionStyle.outlineColor}`
                    : undefined,
                }}
              >
                {activeSubtitle.text}
              </span>
            </div>
          </div>
        )}
        {imageUrl && (
          <div className="absolute bottom-10 w-full flex justify-center">
            <img alt={imageUrl} src={imageUrl} className="h-[60%] w-[60%]" />
          </div>
        )}

        {/* Pause overlay — dark scrim + mic, matches GrammarResponse.tsx pauseOverlay
            (rgba(10, 8, 30, 0.52)) sitting on top of the video, not below it */}
        {isPaused && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 z-10"
            style={{ backgroundColor: "rgba(10, 8, 30, 0.52)" }}
          >
            <button
              type="button"
              onClick={() => setIsAnswerModalOpen(true)}
              className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
              style={{ background: "linear-gradient(180deg, #FF8500 0%, #FFB800 100%)" }}
            >
              <IoMic className="text-white text-6xl p-2" />
            </button>
          </div>
        )}

        {/* "Your turn" badge — matches GrammarResponse.tsx's yourTurnBadge (white pill, #2626A5 18px bold). */}
        {isPaused && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 rounded-full bg-white px-2 py-2">
            <span className="text-lg font-bold" style={{ color: "#2626A5" }}>
              Your turn
            </span>
          </div>
        )}
      </div>

      {/* Answer Modal — shared grading test; closing it resumes the video. */}
      {isAnswerModalOpen && currentCheckpoint && (
        <GradingTestModal
          isOpen={isAnswerModalOpen}
          onClose={handleContinue}
          fields={[
            { label: "Question", value: currentCheckpoint.question },
            { label: "Expected Answer", value: currentCheckpoint.answer },
          ]}
          question={currentCheckpoint.question ?? ""}
          correctAnswer={currentCheckpoint.answer ?? ""}
          mode={currentCheckpoint.gradingMode}
          toastId="gradingToastResponse"
        />
      )}
    </LiveMobileViewContainer>
  );
}
